/**
 * Event-Bus für Echtzeit-Datenaktualisierungen
 *
 * Wird verwendet, um UI-Komponenten zu benachrichtigen, wenn der KI-Assistent
 * oder andere Prozesse Daten ändern (z.B. Kunden erstellen, Anfragen hinzufügen).
 */

// Event-Typen für verschiedene Datenänderungen
export const DATA_REFRESH_EVENTS = {
  // Kunden
  KUNDE_CREATED: 'data:kunde:created',
  KUNDE_UPDATED: 'data:kunde:updated',
  KUNDE_DELETED: 'data:kunde:deleted',

  // Anfragen
  ANFRAGE_CREATED: 'data:anfrage:created',
  ANFRAGE_UPDATED: 'data:anfrage:updated',
  ANFRAGE_DELETED: 'data:anfrage:deleted',
  ANFRAGE_STATUS_CHANGED: 'data:anfrage:status-changed',

  // Termine
  TERMIN_CREATED: 'data:termin:created',
  TERMIN_UPDATED: 'data:termin:updated',
  TERMIN_DELETED: 'data:termin:deleted',

  // Rechnungen
  RECHNUNG_CREATED: 'data:rechnung:created',
  RECHNUNG_UPDATED: 'data:rechnung:updated',
  RECHNUNG_STATUS_CHANGED: 'data:rechnung:status-changed',

  // Kostenvoranschläge
  KVA_CREATED: 'data:kva:created',
  KVA_UPDATED: 'data:kva:updated',

  // Fotos
  FOTO_SAVED: 'data:foto:saved',

  // Katalog
  KATALOG_UPDATED: 'data:katalog:updated',

  // Generisch - für allgemeine Aktualisierungen
  REFRESH_ALL: 'data:refresh:all',
};

/**
 * Dispatcht ein Daten-Refresh-Event
 * @param {string} eventType - Event-Typ aus DATA_REFRESH_EVENTS
 * @param {object} detail - Zusätzliche Daten (z.B. { id, kundeId })
 */
export function dispatchDataRefresh(eventType, detail = {}) {
  const event = new CustomEvent(eventType, {
    detail: {
      ...detail,
      timestamp: Date.now(),
    },
    bubbles: true,
  });

  window.dispatchEvent(event);

  // Debug-Log in Development
  if (import.meta.env.DEV) {
    console.log(`[DataRefresh] ${eventType}`, detail);
  }
}

/**
 * Abonniert ein Daten-Refresh-Event
 * @param {string} eventType - Event-Typ aus DATA_REFRESH_EVENTS
 * @param {function} callback - Callback-Funktion mit (event) => void
 * @returns {function} Cleanup-Funktion zum Abbestellen
 */
export function subscribeToDataRefresh(eventType, callback) {
  window.addEventListener(eventType, callback);

  return () => {
    window.removeEventListener(eventType, callback);
  };
}

/**
 * Abonniert mehrere Events gleichzeitig
 * @param {string[]} eventTypes - Array von Event-Typen
 * @param {function} callback - Callback-Funktion
 * @returns {function} Cleanup-Funktion
 */
export function subscribeToMultipleEvents(eventTypes, callback) {
  eventTypes.forEach(type => window.addEventListener(type, callback));

  return () => {
    eventTypes.forEach(type => window.removeEventListener(type, callback));
  };
}

/**
 * Mappt KI-Assistent Tool-Namen auf entsprechende Events
 */
const TOOL_TO_EVENT_MAP = {
  // Kunden
  'kunde_anlegen': DATA_REFRESH_EVENTS.KUNDE_CREATED,
  'kunde_bearbeiten': DATA_REFRESH_EVENTS.KUNDE_UPDATED,

  // Anfragen
  'anfrage_erstellen': DATA_REFRESH_EVENTS.ANFRAGE_CREATED,
  'anfrage_status_aendern': DATA_REFRESH_EVENTS.ANFRAGE_STATUS_CHANGED,

  // Termine
  'termin_erstellen': DATA_REFRESH_EVENTS.TERMIN_CREATED,
  'termin_aktualisieren': DATA_REFRESH_EVENTS.TERMIN_UPDATED,

  // Rechnungen
  'rechnung_erstellen': DATA_REFRESH_EVENTS.RECHNUNG_CREATED,
  'rechnung_status_aendern': DATA_REFRESH_EVENTS.RECHNUNG_STATUS_CHANGED,

  // KVA
  'kostenvoranschlag_erstellen': DATA_REFRESH_EVENTS.KVA_CREATED,
  'kva_erstellen': DATA_REFRESH_EVENTS.KVA_CREATED,

  // Fotos
  'foto_speichern': DATA_REFRESH_EVENTS.FOTO_SAVED,
};

/**
 * Verarbeitet Tool-Ergebnisse vom KI-Assistenten und dispatcht entsprechende Events
 * @param {array} toolResults - Array von Tool-Ergebnissen aus der Chat-API Response
 */
export function processToolResults(toolResults) {
  if (!toolResults || !Array.isArray(toolResults)) return;

  const dispatchedEvents = new Set();

  toolResults.forEach(toolResult => {
    try {
      const result = typeof toolResult.content === 'string'
        ? JSON.parse(toolResult.content)
        : toolResult.content;

      // Finde den Tool-Namen aus dem tool_call_id oder aus den Namen
      const toolName = toolResult.name || extractToolName(toolResult);

      if (!toolName) return;

      // Prüfe ob die Aktion erfolgreich war
      if (result.success === false || result.error) return;

      // Finde das passende Event
      const eventType = TOOL_TO_EVENT_MAP[toolName];

      if (eventType && !dispatchedEvents.has(eventType)) {
        dispatchDataRefresh(eventType, {
          toolName,
          result,
          kundeId: result.kundeId || result.kunde?.id || result.data?.kunde?.id,
          anfrageId: result.anfrageId || result.anfrage?.id || result.data?.anfrage?.id,
          terminId: result.terminId || result.termin?.id,
          rechnungId: result.rechnungId || result.rechnung?.id,
          kvaId: result.kvaId || result.kva?.id,
          fotoId: result.fotoId || result.data?.fotoId,
          projektId: result.projektId || result.projekt?.id || result.data?.projekt?.id,
        });
        dispatchedEvents.add(eventType);
      }
    } catch (e) {
      // JSON parse error - ignorieren
    }
  });

  // Wenn irgendwelche Datenänderungen stattfanden, auch ein generisches Refresh-Event senden
  if (dispatchedEvents.size > 0) {
    dispatchDataRefresh(DATA_REFRESH_EVENTS.REFRESH_ALL, {
      events: Array.from(dispatchedEvents),
    });
  }
}

/**
 * Hilfsfunktion um Tool-Namen zu extrahieren
 */
function extractToolName(toolResult) {
  // Falls der Name direkt verfügbar ist
  if (toolResult.name) return toolResult.name;

  // Versuche aus dem content zu extrahieren
  try {
    const content = typeof toolResult.content === 'string'
      ? JSON.parse(toolResult.content)
      : toolResult.content;
    return content.tool || content.toolName;
  } catch {
    return null;
  }
}
